import '../../../../packages/neb-lit-components/src/components/neb-selection-card';

import { LitElement, html, css } from 'lit';

import { deduplicate } from '../../../../packages/neb-www-booking/src/components/util/enable-booking-util';
import { baseStyles, CSS_SPACING } from '../../../styles';

export const ELEMENTS = {
  appointmentCheckInCard: { id: 'card-check-in', tag: 'neb-selection-card' },
  appointmentCheckInDescription: { id: 'description-check-in' },
  bookAppointmentCard: {
    id: 'card-book-appointment',
    tag: 'neb-selection-card',
  },
  bookAppointmentDescription: { id: 'description-book-appointment' },
  manageMyAppointmentsCard: {
    id: 'card-my-appointments',
    tag: 'neb-selection-card',
  },
  manageMyAppointmentsDescription: { id: 'description-my-appointments' },
  manageMyPaymentsCard: { id: 'card-my-payments', tag: 'neb-selection-card' },
  manageMyPaymentsDescription: { id: 'description-my-payments' },
  walkInAppointmentCard: {
    id: 'walk-in-appointment',
    tag: 'neb-selection-card',
  },
  walkInAppointmentDescription: { id: 'description-walk-in-appointment' },
};

export const SELF_CHECK_IN_INTERVAL_MESSAGE = {
  288: 'anytime the same day of your appointment.',
  6: 'up to 30 minutes before your scheduled appointment time.',
  12: 'up to 1 hour before your scheduled appointment time.',
  24: 'up to 2 hours before your scheduled appointment time.',
  36: 'up to 3 hours before your scheduled appointment time.',
  48: 'up to 4 hours before your scheduled appointment time.',
  60: 'up to 5 hours before your scheduled appointment time.',
  72: 'up to 6 hours before your scheduled appointment time.',
};

const getGuestAppointmentCheckInCard = selfCheckInIntervalMessage => ({
  id: ELEMENTS.appointmentCheckInCard.id,
  route: '/check-in',
  title: 'Appointment Check In',
  content: html`
    <p id="${ELEMENTS.appointmentCheckInDescription.id}">
      Check yourself in ${selfCheckInIntervalMessage}
    </p>
  `,
});

const getMemberAppointmentCheckInCard = selfCheckInIntervalMessage => ({
  ...getGuestAppointmentCheckInCard(selfCheckInIntervalMessage),
  route: '/my-appointments',
});

const CARD_BOOK_APPOINTMENT = {
  id: ELEMENTS.bookAppointmentCard.id,
  route: '/book-appointment',
  title: 'Book an Appointment',
  content: html`
    <p id="${ELEMENTS.bookAppointmentDescription.id}">
      Use our online portal to find an appointment that’s convenient for you.
    </p>
  `,
};

const CARD_MY_APPOINTMENTS = {
  id: ELEMENTS.manageMyAppointmentsCard.id,
  route: '/my-appointments',
  title: 'Manage My Appointments',
  content: html`
    <p id="${ELEMENTS.manageMyAppointmentsDescription.id}">
      Oversee all your appointments in one place. Make changes to your
      appointments if needed.
    </p>
  `,
};

const CARD_MY_PAYMENTS = {
  id: ELEMENTS.manageMyPaymentsCard.id,
  route: '/my-payments',
  title: 'Manage My Payments',
  content: html`
    <p id="${ELEMENTS.manageMyPaymentsDescription.id}">
      View your current balance and make a payment easily and securely.
    </p>
  `,
};

const CARD_WALK_IN_APPOINTMENT = {
  id: ELEMENTS.walkInAppointmentCard.id,
  route: '/walk-in',
  title: 'Walk-In Appointment',
  content: html`
    <p id="${ELEMENTS.walkInAppointmentDescription.id}">
      Schedule yourself a walk-in appointment.
    </p>
  `,
};

const getGuestCards = selfCheckInIntervalMessage => [
  getGuestAppointmentCheckInCard(selfCheckInIntervalMessage),
];
const getMemberCards = selfCheckInIntervalMessage => [
  getMemberAppointmentCheckInCard(selfCheckInIntervalMessage),
  CARD_MY_APPOINTMENTS,
];

class NebPageLanding extends LitElement {
  static get properties() {
    return {
      __cardConfig: Array,
      user: Boolean,
      showMyPayments: Boolean,
      enableBooking: Boolean,
      enableWalkIn: Boolean,
      enableSelfCheckIn: Boolean,
      selfCheckInInterval: Number,
    };
  }

  static get styles() {
    return [
      baseStyles,
      css`
        :host {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .container {
          display: grid;
          grid-gap: ${CSS_SPACING};
        }
      `,
    ];
  }

  constructor() {
    super();

    this.__initState();
    this.__initHandlers();
  }

  __initState() {
    this.user = {};

    this.onNavigate = () => {};

    this.showMyPayments = false;
    this.enableBooking = false;
    this.enableWalkIn = false;
    this.enableSelfCheckIn = true;
    this.selfCheckInInterval = 12;
  }

  __initHandlers() {
    this.__handlers = {
      navigate: route => this.onNavigate({ route }),
    };
  }

  addCardItems() {
    if (this.enableBooking) {
      this.__cardConfig.splice(1, 0, CARD_BOOK_APPOINTMENT);
    }

    if (this.enableWalkIn) {
      this.__cardConfig.splice(1, 0, CARD_WALK_IN_APPOINTMENT);
    }

    if (this.showMyPayments) {
      this.__cardConfig = [...this.__cardConfig, CARD_MY_PAYMENTS];
    }

    this.__cardConfig = deduplicate(this.__cardConfig, 'id');
  }

  update(changedProps) {
    if (changedProps.has('user') || changedProps.has('selfCheckInInterval')) {
      const selfCheckInIntervalMessage =
        SELF_CHECK_IN_INTERVAL_MESSAGE[this.selfCheckInInterval];

      if (Object.values(this.user).some(Boolean)) {
        this.__cardConfig = [...getMemberCards(selfCheckInIntervalMessage)];
      } else {
        this.__cardConfig = [...getGuestCards(selfCheckInIntervalMessage)];
      }
    }

    this.addCardItems();

    if (changedProps.has('enableSelfCheckIn') && !this.enableSelfCheckIn) {
      this.__cardConfig.splice(0, 1);
    }

    super.update(changedProps);
  }

  render() {
    return html`
      <div class="container">
        ${this.__cardConfig.map(
          card => html`
            <neb-selection-card
              id="${card.id}"
              .name="${card.route}"
              .title="${card.title}"
              .onClick="${this.__handlers.navigate}"
              >${card.content}</neb-selection-card
            >
          `,
        )}
      </div>
    `;
  }
}
customElements.define('neb-page-landing', NebPageLanding);
